import { useEnvironment } from '../contexts/EnvironmentContext';
import { Environment } from '../config/environments';

interface EnvOption {
  value: Environment;
  label: string;
}

const ENV_OPTIONS: EnvOption[] = [
  { value: 'dev', label: 'Dev' },
  { value: 'test', label: 'Test' },
  { value: 'prod', label: 'Prod' },
];

export interface PageEnvironmentSelectorProps {
  /** Optional label rendered before the pills. */
  label?: string;
}

/**
 * In-page environment selector (Dev | Test | Prod).
 *
 * Environment selection is global (EnvironmentContext + the API client inject
 * the x-environment header on every request) but the control now lives on the
 * page rather than the header, mirroring the osdu-insights pattern.
 *
 * Switching environments requires a new OSDU token (per-env MSAL scope), so
 * `switchEnvironment` refreshes the token and then remounts only the page
 * content (skeleton + re-fetch) while the shell stays put — no full reload.
 * Clicking a pill for the already-active environment is a no-op.
 */
export const PageEnvironmentSelector = ({ label = 'Environment' }: PageEnvironmentSelectorProps) => {
  const { currentEnvironment, switchEnvironment, isSwitching } = useEnvironment();

  return (
    <div className="flex items-center gap-2">
      {label && (
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      )}
      <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit">
        {ENV_OPTIONS.map((opt) => {
          const active = currentEnvironment === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => switchEnvironment(opt.value)}
              disabled={isSwitching}
              aria-pressed={active}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                active
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PageEnvironmentSelector;
